import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, X, Play, RefreshCw, Users, Activity, DollarSign, CheckCircle, AlertCircle, Loader2, Info, ChevronRight, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface ApifyRun {
  id: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  usageTotalUsd?: number;
  actId?: string;
}

type StepStatus = 'idle' | 'running' | 'success' | 'error';

interface PipelineState {
  step1: StepStatus;
  step2: StepStatus;
  step1Message: string | null;
  step2Message: string | null;
  pendingCount: number;
}

export default function PepitesConfig() {
  const navigate = useNavigate();
  const [seedAccounts, setSeedAccounts] = useState<string[]>([]);
  const [newSeedAccount, setNewSeedAccount] = useState('');
  const [selectedSeed, setSelectedSeed] = useState<string | null>(null);
  const [pipeline, setPipeline] = useState<PipelineState>({
    step1: 'idle',
    step2: 'idle',
    step1Message: null,
    step2Message: null,
    pendingCount: 0,
  });
  const [apifyRuns, setApifyRuns] = useState<ApifyRun[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [monthlyStats, setMonthlyStats] = useState({ totalRuns: 0, totalCost: 0 });

  useEffect(() => {
    const stored = localStorage.getItem('pepites_seed_accounts');
    if (stored) {
      try { setSeedAccounts(JSON.parse(stored)); } catch { /* ignore */ }
    }
    loadApifyRuns();
    fetchPendingCount();
  }, []);

  const fetchPendingCount = async () => {
    const { count } = await supabase
      .from('rising_creators')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending_profile_scrape');
    setPipeline(prev => ({ ...prev, pendingCount: count || 0 }));
  };

  const loadApifyRuns = async () => {
    setLoadingRuns(true);
    try {
      const APIFY_TOKEN = import.meta.env.VITE_APIFY_TOKEN;
      if (!APIFY_TOKEN) return;
      const res = await fetch(
        `https://api.apify.com/v2/actor-runs?token=${APIFY_TOKEN}&limit=10&desc=1`
      );
      if (res.ok) {
        const data = await res.json();
        const runs: ApifyRun[] = data.data?.items || [];
        setApifyRuns(runs);
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthRuns = runs.filter(r => new Date(r.startedAt) >= firstOfMonth);
        const totalCost = monthRuns.reduce((s, r) => s + (r.usageTotalUsd || 0), 0);
        setMonthlyStats({ totalRuns: monthRuns.length, totalCost: Number(totalCost.toFixed(3)) });
      }
    } catch { /* ignore */ }
    finally { setLoadingRuns(false); }
  };

  const saveSeedAccounts = (accounts: string[]) => {
    setSeedAccounts(accounts);
    localStorage.setItem('pepites_seed_accounts', JSON.stringify(accounts));
  };

  const addSeedAccount = () => {
    const account = newSeedAccount.replace(/^@/, '').trim();
    if (account && !seedAccounts.includes(account)) {
      saveSeedAccounts([...seedAccounts, account]);
      setNewSeedAccount('');
      if (!selectedSeed) setSelectedSeed(account);
    }
  };

  const removeSeedAccount = (account: string) => {
    const next = seedAccounts.filter(a => a !== account);
    saveSeedAccounts(next);
    if (selectedSeed === account) setSelectedSeed(next[0] || null);
  };

  const handleStep1 = async () => {
    if (!selectedSeed) return;
    setPipeline(prev => ({ ...prev, step1: 'running', step1Message: 'Lancement du run Apify...' }));
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Étape 1a : lancer le run
      const launchRes = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-followings`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: selectedSeed }),
        }
      );
      const launchData = await launchRes.json();

      if (!launchData.success) {
        setPipeline(prev => ({
          ...prev,
          step1: 'error',
          step1Message: launchData.error || launchData.details || 'Erreur lors du lancement Apify',
        }));
        return;
      }

      const jobId = launchData.job_id;
      setPipeline(prev => ({
        ...prev,
        step1Message: `Run Apify démarré (ID: ${launchData.run_id?.slice(0, 8)}...). Scrape en cours, cela peut prendre 2-5 minutes...`,
      }));

      // Étape 1b : poller jusqu'à la fin
      let attempts = 0;
      const poll = async () => {
        attempts++;
        if (attempts > 60) {
          setPipeline(prev => ({ ...prev, step1: 'error', step1Message: 'Timeout — le run Apify prend trop de temps. Réessayez plus tard.' }));
          return;
        }

        const pollRes = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-followings`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ job_id: jobId }),
          }
        );
        const pollData = await pollRes.json();

        if (pollData.ready === false) {
          // Encore en cours, réessayer dans 8 secondes
          setTimeout(poll, 8000);
          return;
        }

        if (pollData.success) {
          setPipeline(prev => ({
            ...prev,
            step1: 'success',
            step1Message: `${pollData.saved} comptes sauvegardés sur ${pollData.total_followings} followings de @${selectedSeed}`,
            pendingCount: pollData.saved,
          }));
          setTimeout(loadApifyRuns, 2000);
        } else {
          setPipeline(prev => ({
            ...prev,
            step1: 'error',
            step1Message: pollData.error || 'Erreur lors du traitement des résultats',
          }));
        }
      };

      setTimeout(poll, 10000);

    } catch {
      setPipeline(prev => ({ ...prev, step1: 'error', step1Message: 'Erreur de connexion' }));
    }
  };

  const handleStep2 = async () => {
    setPipeline(prev => ({ ...prev, step2: 'running', step2Message: 'Lancement du run Apify...' }));
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

      const launchRes = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-profiles-from-followings`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ batchSize: 20 }),
        }
      );
      const launchData = await launchRes.json();

      if (!launchData.success) {
        setPipeline(prev => ({ ...prev, step2: 'error', step2Message: launchData.error || launchData.details || 'Erreur lors du lancement' }));
        return;
      }

      if (!launchData.started) {
        setPipeline(prev => ({ ...prev, step2: 'success', step2Message: launchData.message || 'Aucun profil à scraper' }));
        return;
      }

      const { run_id, dataset_id, creator_ids, total } = launchData;
      setPipeline(prev => ({
        ...prev,
        step2Message: `Run Apify démarré pour ${total} profils. Scrape en cours, cela peut prendre quelques minutes...`,
      }));

      let attempts = 0;
      const poll = async () => {
        attempts++;
        if (attempts > 60) {
          setPipeline(prev => ({ ...prev, step2: 'error', step2Message: 'Timeout — le run Apify prend trop de temps.' }));
          return;
        }

        const pollRes = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-profiles-from-followings`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ run_id, dataset_id, creator_ids }),
          }
        );
        const pollData = await pollRes.json();

        if (pollData.ready === false) {
          setTimeout(poll, 10000);
          return;
        }

        if (pollData.success) {
          setPipeline(prev => ({
            ...prev,
            step2: 'success',
            step2Message: `${pollData.added} créateur(s) ajoutés à vos influenceurs, ${pollData.rejected} rejetés`,
            pendingCount: Math.max(0, prev.pendingCount - total),
          }));
          setTimeout(loadApifyRuns, 3000);
        } else {
          setPipeline(prev => ({ ...prev, step2: 'error', step2Message: pollData.error || 'Erreur lors du traitement des résultats' }));
        }
      };

      setTimeout(poll, 15000);

    } catch {
      setPipeline(prev => ({ ...prev, step2: 'error', step2Message: 'Erreur de connexion' }));
    }
  };

  const getStatusIcon = (status: StepStatus) => {
    if (status === 'running') return <Loader2 className="w-4 h-4 animate-spin text-white/60" />;
    if (status === 'success') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'error') return <AlertCircle className="w-4 h-4 text-red-500" />;
    return null;
  };

  const getRunStatusIcon = (status: string) => {
    if (status === 'SUCCEEDED') return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
    if (status === 'FAILED') return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
    return <Loader2 className="w-3.5 h-3.5 text-white/40 animate-spin" />;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <div className="border-b border-white/[0.08] px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/pepites')}
            className="flex items-center gap-2 text-white/50 hover:text-white text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux Pépites
          </button>
          <span className="text-white/20">/</span>
          <h1 className="text-base font-bold text-white">Pipeline de détection</h1>
        </div>
      </div>

      <div className="flex-1 px-6 py-6 max-w-3xl mx-auto w-full space-y-6">

        {/* Stats mensuelles */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-white/[0.06] rounded-xl flex items-center justify-center">
              <Activity className="w-5 h-5 text-white/60" />
            </div>
            <div>
              <div className="text-xl font-bold text-white">{monthlyStats.totalRuns}</div>
              <div className="text-xs text-white/40">Runs Apify ce mois</div>
            </div>
          </div>
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-white/[0.06] rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white/60" />
            </div>
            <div>
              <div className="text-xl font-bold text-white">${monthlyStats.totalCost}</div>
              <div className="text-xs text-white/40">Coût Apify ce mois</div>
            </div>
          </div>
        </div>

        {/* Comptes graines */}
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-white/60" />
            <h2 className="text-sm font-bold text-white">Comptes graines</h2>
            <span className="text-xs text-white/30">Créateurs déjà signés dont on analyse les followings</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {seedAccounts.length === 0 && (
              <span className="text-xs text-white/30 italic">Aucun compte configuré</span>
            )}
            {seedAccounts.map(account => (
              <button
                key={account}
                onClick={() => setSelectedSeed(account)}
                className={`flex items-center gap-1.5 px-3 py-1.5 border text-sm rounded-full transition-colors ${
                  selectedSeed === account
                    ? 'bg-white text-black border-white font-semibold'
                    : 'bg-white/[0.08] border-white/[0.08] text-white/60 hover:bg-white/[0.14]'
                }`}
              >
                @{account}
                <span
                  onClick={e => { e.stopPropagation(); removeSeedAccount(account); }}
                  className="hover:text-red-400 transition-colors ml-0.5"
                >
                  <X className="w-3 h-3" />
                </span>
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newSeedAccount}
              onChange={e => setNewSeedAccount(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSeedAccount()}
              placeholder="@username d'un créateur signé"
              className="flex-1 px-3 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-sm text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none"
            />
            <button
              onClick={addSeedAccount}
              className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-white/90 text-black text-sm font-semibold rounded-xl transition-colors"
            >
              <Plus className="w-4 h-4" /> Ajouter
            </button>
          </div>

          {selectedSeed && (
            <div className="text-xs text-white/50 flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2">
              <Info className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
              Le pipeline analysera les followings de <strong className="text-white">@{selectedSeed}</strong>. Cliquez sur un autre compte pour en changer.
            </div>
          )}
        </div>

        {/* Pipeline */}
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            Lancer le pipeline
          </h2>

          {/* Étape 1 */}
          <div className="border border-white/[0.08] rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-white/[0.04]">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-white/[0.08] text-white/60 text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                <div>
                  <div className="text-sm font-semibold text-white">Récupérer les followings</div>
                  <div className="text-xs text-white/40">
                    {selectedSeed
                      ? `Scrape les comptes suivis par @${selectedSeed} via Apify`
                      : 'Sélectionnez un compte graine ci-dessus'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(pipeline.step1)}
                <button
                  onClick={handleStep1}
                  disabled={!selectedSeed || pipeline.step1 === 'running'}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-white/90 text-black text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {pipeline.step1 === 'running'
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> En cours...</>
                    : <><Play className="w-3.5 h-3.5" /> Lancer</>}
                </button>
              </div>
            </div>
            {pipeline.step1Message && (
              <div className={`px-4 py-2.5 text-xs border-t ${
                pipeline.step1 === 'error'
                  ? 'bg-red-500/10 border-red-500/20 text-red-400'
                  : 'bg-green-500/10 border-green-500/20 text-green-400'
              }`}>
                {pipeline.step1Message}
              </div>
            )}
          </div>

          <div className="flex items-center justify-center text-white/20">
            <ChevronRight className="w-4 h-4 rotate-90" />
          </div>

          {/* Filtre texte (automatique) */}
          <div className="border border-dashed border-white/[0.08] rounded-xl px-4 py-3 flex items-center gap-3 bg-white/[0.02]">
            <span className="w-6 h-6 rounded-full bg-white/[0.06] text-white/40 text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <div>
              <div className="text-sm font-semibold text-white/60">Filtre automatique</div>
              <div className="text-xs text-white/30">Exclusion shops, brands, comptes privés, mots-clés blacklistés — appliqué instantanément</div>
            </div>
            <CheckCircle className="w-4 h-4 text-white/20 ml-auto flex-shrink-0" />
          </div>

          <div className="flex items-center justify-center text-white/20">
            <ChevronRight className="w-4 h-4 rotate-90" />
          </div>

          {/* Étape 2 */}
          <div className="border border-white/[0.08] rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-white/[0.04]">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                <div>
                  <div className="text-sm font-semibold text-white">Scraper les profils + filtre final</div>
                  <div className="text-xs text-white/40">
                    {pipeline.pendingCount > 0
                      ? <span className="text-amber-400 font-medium">{pipeline.pendingCount} profils en attente</span>
                      : '≥10k followers, 3 posts/30j → ajout dans Influenceurs'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(pipeline.step2)}
                <button
                  onClick={handleStep2}
                  disabled={pipeline.step2 === 'running' || pipeline.pendingCount === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.08] hover:bg-white/[0.14] text-white/60 text-xs font-semibold rounded-lg border border-white/[0.08] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {pipeline.step2 === 'running'
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> En cours...</>
                    : <><Zap className="w-3.5 h-3.5" /> Enrichir ({pipeline.pendingCount})</>}
                </button>
              </div>
            </div>
            {pipeline.step2Message && (
              <div className={`px-4 py-2.5 text-xs border-t ${
                pipeline.step2 === 'error'
                  ? 'bg-red-500/10 border-red-500/20 text-red-400'
                  : 'bg-green-500/10 border-green-500/20 text-green-400'
              }`}>
                {pipeline.step2Message}
              </div>
            )}
          </div>
        </div>

        {/* Comment ça fonctionne */}
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-white/40" />
            <h3 className="text-sm font-bold text-white/60">Comment fonctionne le pipeline</h3>
          </div>
          <ol className="space-y-2.5">
            {[
              { n: 1, text: "Apify récupère la liste des comptes suivis par votre créateur graine via l'acteur scraping_solutions/instagram-scraper-followers-following-no-cookies." },
              { n: 2, text: "Filtre instantané dans le code : exclusion des shops, brands, agences, comptes privés et tout compte dont le nom/username contient un mot-clé blacklisté." },
              { n: 3, text: "Les comptes restants sont sauvegardés en base en attente de scrape profil." },
              { n: 4, text: "Apify scrape les profils complets (bio, followers, derniers posts) via apify/instagram-scraper en mode profil — batch de 20 par défaut pour maîtriser les coûts." },
              { n: 5, text: "Filtre final : ≥ 10 000 followers ET au moins 3 posts dans les 30 derniers jours. Les créateurs validés sont directement ajoutés à votre liste Influenceurs." },
            ].map(step => (
              <li key={step.n} className="flex items-start gap-3 text-sm text-white/40">
                <span className="w-5 h-5 rounded-full bg-white/[0.08] text-white/50 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {step.n}
                </span>
                {step.text}
              </li>
            ))}
          </ol>
        </div>

        {/* Derniers runs */}
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Activity className="w-4 h-4 text-white/40" />
              Derniers runs Apify
            </h2>
            <button
              onClick={loadApifyRuns}
              disabled={loadingRuns}
              className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingRuns ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>

          {loadingRuns ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-white/40" />
            </div>
          ) : apifyRuns.length === 0 ? (
            <div className="text-center py-6 text-sm text-white/30">
              Aucun run récent — configurez VITE_APIFY_TOKEN pour afficher l'historique
            </div>
          ) : (
            <div className="space-y-2">
              {apifyRuns.map(run => (
                <div key={run.id} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
                  <div className="flex items-center gap-2">
                    {getRunStatusIcon(run.status)}
                    <span className="text-sm text-white/60">{run.status === 'SUCCEEDED' ? 'Succès' : run.status === 'FAILED' ? 'Échec' : run.status === 'RUNNING' ? 'En cours' : run.status}</span>
                    <span className="text-xs text-white/30">{run.id.slice(0, 8)}...</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-white/40">
                    <span>{new Date(run.startedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    {run.usageTotalUsd !== undefined && (
                      <span className="font-medium text-white/60">${run.usageTotalUsd.toFixed(3)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
